'use client';

import type { ArchitectureState } from '@/lib/architecture-state';

interface RowProps {
  label: string;
  pct: number;
  color: string;
}

function fmtCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

function Row({ label, pct, color }: RowProps): React.JSX.Element {
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-[10px]">
        <span className="text-white/70">{label}</span>
        <span className="tabular-nums text-white/90">{(pct * 100).toFixed(1)}%</span>
      </div>
      <div className="h-1.5 rounded-full bg-zinc-800 overflow-hidden">
        <div
          className="h-full transition-[width] duration-500 ease-out"
          style={{ width: `${(pct * 100).toFixed(1)}%`, backgroundColor: color }}
        />
      </div>
    </div>
  );
}

export function ResolverPinch({ resolver }: { resolver: ArchitectureState['resolver'] }): React.JSX.Element {
  return (
    <div className="flex flex-col items-center gap-1.5 px-2">
      <div className="text-[9px] uppercase tracking-wider text-white/40">Resolver</div>

      <svg width="72" height="100" viewBox="0 0 120 180" className="block" aria-hidden>
        <defs>
          <linearGradient id="pinchGrad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#c9a84c" stopOpacity="0.7" />
            <stop offset="100%" stopColor="#c9a84c" stopOpacity="0.2" />
          </linearGradient>
        </defs>
        <polygon points="10,10 110,90 10,170" fill="url(#pinchGrad)" stroke="#c9a84c" strokeOpacity="0.5" strokeWidth="1.5" />
        <line x1="110" y1="90" x2="120" y2="90" stroke="#c9a84c" strokeWidth="1.5" />
      </svg>

      <div className="text-[9px] text-white/60 tabular-nums">
        {fmtCount(resolver.fieldCount)} merged
      </div>

      <div className="w-full max-w-[160px] space-y-1">
        <Row label="BBG" pct={resolver.bbgPct} color="#34d399" />
        <Row label="FMP" pct={resolver.fmpPct} color="#60a5fa" />
        <Row label="Derived" pct={resolver.derivedPct} color="#fbbf24" />
        <Row label="Null" pct={resolver.nullPct} color="#f87171" />
      </div>
    </div>
  );
}
